import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { customerApi, formatApiErrorDetail } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

export default function OrderRequestDialog({ artwork, trigger, reservedOnly = false }) {
  const { customer } = useCustomerAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "anfrage", message: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await customerApi.post("/order-requests", {
        artwork_slug: artwork.slug,
        kind: form.kind,
        message: form.message,
        phone: form.phone,
      });
      toast.success("Deine Anfrage wurde gesendet. Susanne meldet sich bei dir.");
      setOpen(false);
      setForm({ kind: "anfrage", message: "", phone: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const KindBtn = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setForm({ ...form, kind: value })}
      data-testid={`order-kind-${value}`}
      className={`flex-1 h-11 rounded-md border text-sm transition-colors ${
        form.kind === value ? "bg-[#32104F] text-[#FFF9F2] border-[#32104F]" : "bg-white text-[#6E5A82] border-[#E7D8C6] hover:border-[#632A8E]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#FFF9F2] max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-normal">Anfrage zu diesem Werk</DialogTitle>
          <DialogDescription className="text-[#6E5A82]">
            „{artwork.title}“ — als {customer?.name} ({customer?.email}). Susanne meldet sich persönlich bei dir.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="order-request-form">
          <div>
            <Label className="mb-1.5 block">Art der Anfrage</Label>
            {reservedOnly ? (
              <p className="text-sm text-[#6E5A82] bg-[#FFF4D6] border border-[#F4C542]/50 rounded-md p-3" data-testid="reserved-hint">
                Dieses Werk ist aktuell <strong>reserviert</strong> – eine Bestellanfrage ist nicht möglich. Du kannst aber eine unverbindliche Anfrage senden.
              </p>
            ) : (
              <div className="flex gap-2">
                <KindBtn value="anfrage" label="Unverbindliche Anfrage" />
                <KindBtn value="bestellung" label="Bestellanfrage" />
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="or-phone">Telefon (optional)</Label>
            <Input id="or-phone" data-testid="order-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-white" />
          </div>
          <div>
            <Label htmlFor="or-msg">Nachricht *</Label>
            <Textarea id="or-msg" data-testid="order-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-white" placeholder="Deine Nachricht zum Werk…" />
          </div>
          <Button type="submit" data-testid="order-submit" disabled={loading} className="w-full bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md h-12">
            {loading ? "Wird gesendet…" : "Anfrage senden"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
